import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CUIText, Icon, IconNames } from '@dotcom/crown-ui';
import { FontSizeAction } from '../font-drawer/font-drawer-types';
import styles from './font-size-control.module.scss';

export type FontSizeControlProps = {
  initialFontSize: number;
  onChange: (size: number) => void;
  /** Defaults to 5. */
  min?: number;
  /** Defaults to 60. */
  max?: number;
  /** Optional label override; defaults to translated 'fontDrawer.fontSize'. */
  label?: string;
  /** When false, suppresses the visible label above the counter. Defaults to true. */
  showLabel?: boolean;
  testId?: string;
};

/**
 * Standalone font-size counter (+/- buttons). Manages its own preview state
 * and notifies the parent via `onChange` for canvas updates.
 *
 * Has no canvas or context dependencies — drop it anywhere, including
 * Storybook.
 */
export const FontSizeControl = ({
  initialFontSize,
  onChange,
  min = 5,
  max = 60,
  label,
  showLabel = true,
  testId = 'font-size-control',
}: FontSizeControlProps): React.ReactElement => {
  const { t } = useTranslation();
  const [fontSize, setFontSize] = useState(initialFontSize);

  const handleChange = (action: FontSizeAction) => {
    const next = action === FontSizeAction.Increase ? fontSize + 1 : fontSize - 1;
    setFontSize(next);
    onChange(next);
  };

  return (
    <section className={styles['font-size-control']} data-testid={testId}>
      {showLabel && (
        <CUIText as="label" variant="form" className={styles.label}>
          {label ?? (t('fontDrawer.fontSize') as string)}
        </CUIText>
      )}
      <div className={styles.counter}>
        <Button
          testId="decreaseFontButton"
          aria-label={t('fontDrawer.decreaseFontSize') as string}
          disabled={fontSize <= min}
          variant="secondary"
          size="icon"
          onClick={() => handleChange(FontSizeAction.Decrease)}
        >
          <Icon name={IconNames.Remove} />
        </Button>
        <CUIText as="span" color="textPrimary" className={styles.value}>
          {fontSize}
        </CUIText>
        <Button
          testId="increaseFontButton"
          aria-label={t('fontDrawer.increaseFontSize') as string}
          disabled={fontSize >= max}
          variant="secondary"
          size="icon"
          onClick={() => handleChange(FontSizeAction.Increase)}
        >
          <Icon name={IconNames.Expand} />
        </Button>
      </div>
    </section>
  );
};
